/* /crm/contacts/:contactId — één Contact op één pagina (ADR 0022).
 *
 *   kop          terug, vorige/volgende (in de volgorde van de lijst, [ en ]), acties
 *                (mailen, bellen, bewerken, archiveren); naam, functie, organisatie
 *   sectiebalk   Gegevens, Offertes, Collega's, Projecten (van de organisatie)
 *
 * Offertes zijn die waar dit contact op staat (quotes.contact_id). */

import { useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Archive, Building2, Mail, MoreHorizontal, Pencil, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ContactModal } from '@/components/crm/ContactModal';
import { Sectiebalk, type Sectie } from '@/features/projectpagina/ui/Sectiebalk';
import { haalContact, paginaStaat, type ContactPaginaData } from './data/pagina';
import type { RelatieClient } from './data/organisaties';
import { contactLijst } from './data/contacten';
import { archiveerContact } from './data/archiveren';
import { statusLabel } from './data/labels';
import { contactSleutel, naRelatieWijziging } from './data/sleutels';
import { Deel, Gegevenslijst, Navigatie, PaginaMelding, Pil, useRechten } from './ui/Onderdelen';
import { ContactRijen, OfferteRijen, ProjectRijen } from './ui/Blokken';
import { OrgIcoon } from './ui/Lijst';
import { Archiveer } from './ui/Archiveer';

const client = supabase as unknown as RelatieClient;
const DATUM = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
const pad = (id: string) => `/crm/contacts/${id}`;
const telLink = (t: string) => `tel:${t.replace(/[^\d+]/g, '')}`;

function Kop({ d, kopRef, magBewerken, magBeheren, onBewerk, onArchiveer }: {
  d: ContactPaginaData;
  kopRef: React.Ref<HTMLElement>;
  magBewerken: boolean;
  magBeheren: boolean;
  onBewerk: () => void;
  onArchiveer: () => void;
}) {
  const c = d.contact;
  const o = d.organisatie;
  const bel = c.telefoon ?? c.mobiel;
  return (
    <section ref={kopRef} data-contact-kop aria-label="Contact" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Navigatie id={c.id} geheugen={contactLijst} terug="Contacten" pad={pad} enkelvoud="contact" />
        <div className="flex items-center gap-2">
          {c.email && (
            <Button variant="outline" size="sm" className="h-8" asChild>
              <a href={`mailto:${c.email}`}><Mail className="mr-1.5 h-4 w-4" />Mailen</a>
            </Button>
          )}
          {bel && (
            <Button variant="outline" size="sm" className="h-8" asChild>
              <a href={telLink(bel)}><Phone className="mr-1.5 h-4 w-4" />Bellen</a>
            </Button>
          )}
          {magBewerken && (
            <Button size="sm" className="h-8" onClick={onBewerk}>
              <Pencil className="mr-1.5 h-4 w-4" />
              Bewerken
            </Button>
          )}
          {magBeheren && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Meer acties">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <Archiveer
                  wat="contact"
                  naam={c.naam}
                  bezwaar={null}
                  uitleg={d.offertes.length ? `Het staat op ${d.offertes.length === 1 ? 'een offerte' : `${d.offertes.length} offertes`}; die blijven ongewijzigd.` : undefined}
                  onArchiveer={onArchiveer}
                  trigger={
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      <Archive className="mr-2 h-4 w-4" />Archiveren
                    </DropdownMenuItem>
                  }
                />
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="min-w-0 text-2xl font-semibold leading-tight tracking-tight">{c.naam}</h1>
          {c.avg && <Pil className="border-emerald-200 bg-emerald-50 text-emerald-800">AVG-toestemming</Pil>}
        </div>
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {c.functie && <span>{c.functie}</span>}
          {o ? (
            <Link to={`/crm/organisaties/${o.id}`} data-org-link className="inline-flex items-center gap-1.5 font-medium text-foreground hover:underline">
              <OrgIcoon src={o.logo} naam={o.naam} className="h-5 w-5" />
              {o.naam}
              {o.gearchiveerd && <span className="font-normal text-amber-700">(gearchiveerd)</span>}
            </Link>
          ) : (
            <span className="inline-flex items-center gap-1.5"><Building2 className="h-4 w-4" aria-hidden />Geen organisatie</span>
          )}
          {c.email && <a href={`mailto:${c.email}`} className="hover:text-foreground">{c.email}</a>}
          {bel && <a href={telLink(bel)} className="tabular-nums hover:text-foreground">{bel}</a>}
        </p>
      </div>
    </section>
  );
}

function Gegevens({ d }: { d: ContactPaginaData }) {
  const c = d.contact;
  const o = d.organisatie;
  const rijen: [string, ReactNode][] = [
    ['E-mail', c.email ? <a className="hover:underline" href={`mailto:${c.email}`}>{c.email}</a> : '—'],
    ['Telefoon', c.telefoon ? <a className="hover:underline" href={telLink(c.telefoon)}>{c.telefoon}</a> : '—'],
    ['Mobiel', c.mobiel ? <a className="hover:underline" href={telLink(c.mobiel)}>{c.mobiel}</a> : '—'],
    ['Functie', c.functie ?? '—'],
    ['Organisatie', o ? <Link className="hover:underline" to={`/crm/organisaties/${o.id}`}>{o.naam}</Link> : '—'],
    ...(o ? [['Status organisatie', statusLabel(o.status)] as [string, ReactNode]] : []),
    ['AVG-toestemming', c.avg ? 'Ja' : 'Nee'],
    ['In het CRM sinds', c.aangemaakt ? DATUM.format(new Date(c.aangemaakt)) : '—'],
  ];
  return <Gegevenslijst rijen={rijen} />;
}

export default function ContactPagina() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { magBewerken, magBeheren } = useRechten();
  const kopRef = useRef<HTMLElement>(null);
  const [bewerken, setBewerken] = useState(false);
  const q = useQuery({
    queryKey: contactSleutel(contactId),
    queryFn: () => haalContact(client, contactId as string),
    retry: (n, fout: any) => fout?.soort === 'fout' && n < 1,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const staat = paginaStaat(q);

  if (staat.soort !== 'klaar') {
    return (
      <PaginaMelding
        soort={staat.soort}
        fout={'fout' in staat ? staat.fout : undefined}
        lijst={contactLijst.adres()}
        lijstLabel="Contacten"
        opnieuw={staat.soort === 'fout' ? () => q.refetch() : undefined}
      />
    );
  }

  const d = staat.data;
  const c = d.contact;
  const archiveer = async () => {
    try {
      await archiveerContact(client, c.id);
      toast({ title: 'Contact gearchiveerd', description: `"${c.naam}" staat in het Archief.` });
      naRelatieWijziging(qc);
      navigate(contactLijst.adres());
    } catch (e) {
      toast({ title: 'Archiveren mislukt', description: (e as Error)?.message ?? String(e), variant: 'destructive' });
    }
  };

  const secties: Sectie[] = [
    { id: 'gegevens', label: 'Gegevens' },
    { id: 'offertes', label: 'Offertes', aantal: d.offertes.length },
    ...(d.organisatie ? [
      { id: 'collegas', label: "Collega's", aantal: d.collegas.length },
      { id: 'projecten', label: 'Projecten', aantal: d.projecten.length },
    ] : []),
  ];

  return (
    <div className="flex flex-col gap-4" data-relatie-staat="klaar">
      <Kop d={d} kopRef={kopRef} magBewerken={magBewerken} magBeheren={magBeheren} onBewerk={() => setBewerken(true)} onArchiveer={archiveer} />
      <Sectiebalk secties={secties} kop={kopRef} titel={c.naam} label="Secties van dit contact" />
      <Deel id="gegevens" titel="Gegevens">
        <Gegevens d={d} />
      </Deel>
      <Deel id="offertes" titel="Offertes">
        <OfferteRijen offertes={d.offertes} leeg="Dit contact staat op geen offerte." />
      </Deel>
      {d.organisatie && (
        <>
          <Deel id="collegas" titel={`Collega's bij ${d.organisatie.naam}`}>
            <ContactRijen contacten={d.collegas} leeg="Geen andere contactpersonen bij deze organisatie." />
          </Deel>
          <Deel id="projecten" titel={`Projecten van ${d.organisatie.naam}`}>
            <ProjectRijen projecten={d.projecten} leeg="Deze organisatie heeft nog geen projecten." />
          </Deel>
        </>
      )}
      {magBewerken && (
        <ContactModal
          isOpen={bewerken}
          onClose={() => setBewerken(false)}
          contact={c.bron as any}
          onSave={() => naRelatieWijziging(qc)}
        />
      )}
    </div>
  );
}
